import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import {
  TuiInputModule,
  TuiInputNumberModule,
  TuiInputTagModule,
} from '@taiga-ui/kit';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { Store } from '@ngxs/store';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PackageTypeService } from '@shared/services/package-type.service';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { CargoPlaceService } from '@shared/services/cargo-place.service';
import { cargoPlaceTypesMap } from '@shared/constants/cargo-places';
import {
  CargoPlaceQuantityForm,
  PackageType,
} from '../../../../types/package-type';
import { CargoPlace } from '../../../../types/cargo-place';

type InputData = {
  packageType: Nullable<PackageType>;
};

type PreparedCargoPlace = CargoPlace & {
  text: Nullable<string>;
};
@Component({
  selector: 'app-package-type-form',
  standalone: true,
  imports: [
    AsyncPipe,
    TuiButtonModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    AutocompleteComponent,
    TuiInputTagModule,
  ],
  templateUrl: './package-type-form.component.html',
  styleUrl: './package-type-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageTypeFormComponent implements OnInit {
  save$ = new Subject<void>();

  isEdit$ = new BehaviorSubject(false);
  title$ = this.isEdit$.pipe(
    map(value => (value ? 'Редактировать' : 'Создать').concat(' упаковку')),
  );

  packageTypeForm = this.fb.group({
    id: this.fb.control<Nullable<number>>(null),
    name: this.fb.control<Nullable<string>>(null, Validators.required),
    size: '',
    code: '',
    cargoPlaceQuantityJson: this.fb.array<FormGroup<CargoPlaceQuantityForm>>(
      [],
    ),
    weight: this.fb.control<Nullable<number>>(null),
    tags: this.fb.nonNullable.control<string[]>([]),
  });

  readonly typesMap = cargoPlaceTypesMap();

  cargoPlaces$: Observable<PreparedCargoPlace[]> = this.cargoPlaceService
    .getAll()
    .pipe(
      map(items =>
        items.map(item => {
          return {
            ...item,
            text: this.typesMap.get(item.code ?? '')?.name ?? '',
          };
        }),
      ),
      shareReplay(),
    );

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<void, InputData>,
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    private readonly packageTypeService: PackageTypeService,
    private readonly cargoPlaceService: CargoPlaceService,
  ) {}

  onSaveClick() {
    this.save$
      .pipe(
        switchMap(() =>
          this.packageTypeService.update(
            this.packageTypeForm.getRawValue() as PackageType,
          ),
        ),
        tap(() => this.closeOnSave()),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  closeOnSave() {
    this.context.$implicit.next();
    this.context.$implicit.complete();
  }

  addCargoPlaceQuantity() {
    this.packageTypeForm.controls.cargoPlaceQuantityJson.push(
      this.fb.group({
        cargoPlaceId: this.fb.control<Nullable<number>>(null),
        quantity: this.fb.control<Nullable<number>>(null),
      }),
    );
  }

  removeCargoPlaceQuantity(index: number) {
    this.packageTypeForm.controls.cargoPlaceQuantityJson.removeAt(index);
  }

  setForm() {
    if (this.context.data.packageType) {
      const { cargoPlaceQuantityJson, ...rest } = this.context.data.packageType;
      this.packageTypeForm.patchValue(rest);
      this.packageTypeForm.setControl(
        'cargoPlaceQuantityJson',
        this.fb.array(cargoPlaceQuantityJson.map(c => this.fb.group(c))),
      );
    }
  }

  ngOnInit() {
    this.isEdit$.next(!!this.context.data.packageType?.id);
    this.setForm();
    this.onSaveClick();
  }
}
